import { ArrowLeftOutlined, DownloadOutlined, PlusOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Skeleton, Typography } from 'antd';
import { useDeleteMyArtifactMutation, useGetMyArtifactsQuery, useReorderMyArtifactsMutation } from 'api/ArtifactsApi';
import { UserAccessLevelPlanEnum } from 'api/UsersApi';
import ArtifactCarousel from 'components/Artifacts/ArtifactCarousel';
import ArtifactDetailViewer from 'components/Artifacts/ArtifactDetailViewer';
import ArtifactEditorModal from 'components/Artifacts/ArtifactEditorModal';
import ArtifactShareModal from 'components/Artifacts/ArtifactShareModal';
import { ArtifactEditorMode } from 'components/Artifacts/editorTypes';
import { Artifact } from 'components/Artifacts/types';
import useArtifactTranscriptionPolling from 'components/Artifacts/useArtifactTranscriptionPolling';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import { useCompanyContext } from 'contexts/CompanyContext';
import Footer from 'pages/Client/Footer/Footer';
import Header from 'pages/Client/Header/Header';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAuthToken, selectAuthUser } from 'store/authSlice';
import { buildApiDownloadUrl, downloadFileFromApi } from 'utils/downloadFile';
import { userHasAccess } from 'utils/utils';
import 'components/Artifacts/ArtifactsExperience.css';

const { Paragraph, Text, Title } = Typography;
type ReorderDropPosition = 'before' | 'after';
type BrandedToolbarButtonStyle = CSSProperties & {
  '--artifacts-primary-color'?: string;
};

const getArtifactsCollectionDownloadUrl = () => buildApiDownloadUrl('/users/me/artifacts/download');

const buildReorderedItems = (
  items: Artifact[],
  draggedArtifactId: string,
  targetArtifactId: string,
  position: ReorderDropPosition
) => {
  if (draggedArtifactId === targetArtifactId) {
    return null;
  }

  const reorderedItems = [...items];
  const draggedIndex = reorderedItems.findIndex((artifact) => artifact.id === draggedArtifactId);
  const targetIndex = reorderedItems.findIndex((artifact) => artifact.id === targetArtifactId);

  if (draggedIndex < 0 || targetIndex < 0) {
    return null;
  }

  const [draggedArtifact] = reorderedItems.splice(draggedIndex, 1);
  const adjustedTargetIndex = reorderedItems.findIndex((artifact) => artifact.id === targetArtifactId);
  const insertionIndex = position === 'before' ? adjustedTargetIndex : adjustedTargetIndex + 1;

  reorderedItems.splice(insertionIndex, 0, draggedArtifact);

  const hasChanged = reorderedItems.some((artifact, index) => artifact.id !== items[index]?.id);
  return hasChanged ? reorderedItems : null;
};

export function PatientArtifactsPage() {
  const navigate = useNavigate();
  const { companyConfig } = useCompanyContext();
  const user = useSelector(selectAuthUser);
  const accessToken = useSelector(selectAuthToken);
  const hasArtifactsPaidAccess = Boolean(userHasAccess(user, UserAccessLevelPlanEnum.Premium));
  const { data, isLoading, isError, refetch } = useGetMyArtifactsQuery(undefined, {
    skip: !hasArtifactsPaidAccess
  });
  const [deleteArtifact, { isLoading: isDeleting }] = useDeleteMyArtifactMutation();
  const [reorderArtifacts, { isLoading: isReordering }] = useReorderMyArtifactsMutation();
  const [editorMode, setEditorMode] = useState<ArtifactEditorMode>('create');
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [actionErrorMessage, setActionErrorMessage] = useState('');
  const [isDownloadingCollection, setIsDownloadingCollection] = useState(false);

  const items = useMemo(() => {
    if (!hasArtifactsPaidAccess) {
      return [];
    }

    return [...(data?.items || [])].sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data?.items, hasArtifactsPaidAccess]);
  const collectionDownloadUrl = data?.downloadZipUrl?.trim() || (items.length ? getArtifactsCollectionDownloadUrl() : '');

  const selectedArtifact = useMemo(
    () => items.find((artifact) => artifact.id === selectedArtifactId) || items[0] || null,
    [items, selectedArtifactId]
  );
  const addArtifactButtonStyle = useMemo<BrandedToolbarButtonStyle>(
    () => ({
      '--artifacts-primary-color': companyConfig.colorPrimary
    }),
    [companyConfig.colorPrimary]
  );

  const selectedArtifactIndex = selectedArtifact ? items.findIndex((artifact) => artifact.id === selectedArtifact.id) : -1;

  useArtifactTranscriptionPolling({
    artifacts: items,
    refetch
  });

  useEffect(() => {
    if (!items.length) {
      setSelectedArtifactId(null);
      setIsDescriptionExpanded(false);
      setIsTranscriptExpanded(false);
      return;
    }

    if (!selectedArtifactId || !items.some((artifact) => artifact.id === selectedArtifactId)) {
      setSelectedArtifactId(items[0].id);
      setIsDescriptionExpanded(false);
      setIsTranscriptExpanded(false);
    }
  }, [items, selectedArtifactId]);

  useEffect(() => {
    setActionErrorMessage('');
  }, [selectedArtifactId]);

  const openCreateEditor = () => {
    setEditorMode('create');
    setIsEditorOpen(true);
  };

  const openEditEditor = (artifact: Artifact) => {
    setEditorMode('edit');
    setSelectedArtifactId(artifact.id);
    setIsEditorOpen(true);
  };

  const getApiErrorMessage = (error: any, fallback: string) => {
    if (error?.status === 409) {
      return 'This artifact was updated elsewhere. Refresh to review the latest version before saving.';
    }

    return error?.data?.message || fallback;
  };

  const handleRefresh = () => {
    setActionErrorMessage('');
    void refetch();
  };

  const handleDownloadCollection = async () => {
    if (!hasArtifactsPaidAccess) {
      navigate('/pricing');
      return;
    }

    if (!collectionDownloadUrl || isDownloadingCollection) {
      return;
    }

    setActionErrorMessage('');
    setIsDownloadingCollection(true);

    try {
      await downloadFileFromApi({
        url: collectionDownloadUrl,
        fallbackFileName: 'Artifacts-Collection.zip',
        accessToken
      });
    } catch {
      setActionErrorMessage('We couldn’t prepare the download. Please try again.');
    } finally {
      setIsDownloadingCollection(false);
    }
  };

  const handleSelectArtifact = (artifactId: string) => {
    setSelectedArtifactId(artifactId);
    setIsDescriptionExpanded(false);
    setIsTranscriptExpanded(false);
  };

  const handleSelectPrevious = () => {
    if (selectedArtifactIndex > 0) {
      handleSelectArtifact(items[selectedArtifactIndex - 1].id);
    }
  };

  const handleSelectNext = () => {
    if (selectedArtifactIndex >= 0 && selectedArtifactIndex < items.length - 1) {
      handleSelectArtifact(items[selectedArtifactIndex + 1].id);
    }
  };

  const submitReorder = async (reorderedItems: Artifact[]) => {
    if (!data) {
      return;
    }

    setActionErrorMessage('');

    await reorderArtifacts({
      items: reorderedItems.map((artifact, index) => ({
        artifactId: artifact.id,
        displayOrder: index,
        expectedRevision: artifact.revision
      })),
      expectedCollectionRevision: data.revision
    }).unwrap();

    void refetch();
  };

  const handleMoveSelected = async (direction: -1 | 1) => {
    if (!data || !selectedArtifact) {
      return;
    }

    const currentIndex = items.findIndex((artifact) => artifact.id === selectedArtifact.id);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= items.length) {
      return;
    }

    const reorderedItems = [...items];
    const [movedArtifact] = reorderedItems.splice(currentIndex, 1);
    reorderedItems.splice(nextIndex, 0, movedArtifact);

    try {
      await submitReorder(reorderedItems);
      setSelectedArtifactId(movedArtifact.id);
    } catch (error: any) {
      setActionErrorMessage(getApiErrorMessage(error, 'We could not reorder your artifacts right now. Please try again.'));
    }
  };

  const handleDragReorder = async (draggedArtifactId: string, targetArtifactId: string, position: ReorderDropPosition) => {
    if (!data) {
      return;
    }

    const reorderedItems = buildReorderedItems(items, draggedArtifactId, targetArtifactId, position);

    if (!reorderedItems) {
      return;
    }

    try {
      await submitReorder(reorderedItems);
    } catch (error: any) {
      setActionErrorMessage(getApiErrorMessage(error, 'We could not reorder your artifacts right now. Please try again.'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedArtifact) {
      return;
    }

    try {
      setActionErrorMessage('');
      await deleteArtifact({
        artifactId: selectedArtifact.id,
        expectedRevision: selectedArtifact.revision
      }).unwrap();

      setIsDeleteModalOpen(false);
      const fallbackArtifact = items[selectedArtifactIndex - 1] || items[selectedArtifactIndex + 1] || null;
      setSelectedArtifactId(fallbackArtifact?.id || null);
      setIsDescriptionExpanded(false);
      setIsTranscriptExpanded(false);
      void refetch();
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      setActionErrorMessage(getApiErrorMessage(error, 'We could not delete this artifact right now. Please try again.'));
    }
  };

  return (
    <div className="layout-full-height">
      <Header />

      <ContainerComponent className="layout-main-content">
        <div className="artifacts-page">
          <div className="artifacts-page__shell">
            <a href="/home" className="artifacts-page__back-link">
              <ArrowLeftOutlined />
              <span>Back to Home</span>
            </a>

            <div className="artifacts-page__hero">
              <div>
                <p className="artifacts-page__welcome">Welcome.</p>
                <Title className="artifacts-page__title" level={1}>
                  Artifacts
                </Title>
                <Paragraph className="artifacts-page__subtitle">
                  Preserve the stories behind meaningful photos and moments.
                </Paragraph>
              </div>

              <div className="artifacts-page__toolbar">
                <Button icon={<DownloadOutlined />} onClick={() => void handleDownloadCollection()} disabled={!collectionDownloadUrl || isDownloadingCollection}>
                  {isDownloadingCollection ? 'Preparing download...' : 'Download Collection'}
                </Button>
                <Button
                  className="artifacts-page__toolbar-primary"
                  type="primary"
                  icon={<PlusOutlined />}
                  style={addArtifactButtonStyle}
                  onClick={() => {
                    if (!hasArtifactsPaidAccess) {
                      navigate('/pricing');
                      return;
                    }

                    openCreateEditor();
                  }}
                >
                  Add Artifact
                </Button>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => {
                    if (!hasArtifactsPaidAccess) {
                      navigate('/pricing');
                      return;
                    }

                    setIsShareModalOpen(true);
                  }}
                >
                  Share Collection
                </Button>
              </div>
            </div>

            {!hasArtifactsPaidAccess && (
              <Alert
                type="info"
                showIcon
                className="artifacts-page__error"
                message="Upgrade to create and share your Artifacts"
                description="Artifacts are available on Premium and Unlimited plans."
                action={
                  <Button type="primary" onClick={() => navigate('/pricing')}>
                    Upgrade Plan
                  </Button>
                }
              />
            )}

            {hasArtifactsPaidAccess && isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

            {hasArtifactsPaidAccess && !isLoading && isError && (
              <Alert
                type="error"
                showIcon
                className="artifacts-page__error"
                message="We couldn’t load your artifacts right now. Please refresh and try again."
                action={
                  <Button size="small" onClick={handleRefresh}>
                    Refresh
                  </Button>
                }
              />
            )}

            {hasArtifactsPaidAccess && !isLoading && !isError && data && (
              <>
                {actionErrorMessage && (
                  <Alert
                    type="error"
                    showIcon
                    className="artifacts-page__error"
                    message={actionErrorMessage}
                    action={
                      <Button size="small" onClick={handleRefresh}>
                        Refresh
                      </Button>
                    }
                  />
                )}

                <div className="artifacts-page__panel">
                  <ArtifactDetailViewer
                    artifact={selectedArtifact}
                    canMoveEarlier={selectedArtifactIndex > 0}
                    canMoveLater={selectedArtifactIndex >= 0 && selectedArtifactIndex < items.length - 1}
                    isDescriptionExpanded={isDescriptionExpanded}
                    isDeleting={isDeleting}
                    isReordering={isReordering}
                    isTranscriptExpanded={isTranscriptExpanded}
                    onDelete={() => setIsDeleteModalOpen(true)}
                    onToggleDescription={() => setIsDescriptionExpanded((current) => !current)}
                    onEdit={() => selectedArtifact && openEditEditor(selectedArtifact)}
                    onMoveEarlier={() => void handleMoveSelected(-1)}
                    onMoveLater={() => void handleMoveSelected(1)}
                    onToggleTranscript={() => setIsTranscriptExpanded((current) => !current)}
                  />
                </div>

                <div className="artifacts-page__panel">
                  <ArtifactCarousel
                    items={items}
                    isReordering={isReordering}
                    selectedArtifactId={selectedArtifact?.id || null}
                    onReorder={(draggedArtifactId, targetArtifactId, position) => void handleDragReorder(draggedArtifactId, targetArtifactId, position)}
                    onSelect={handleSelectArtifact}
                    onPrevious={handleSelectPrevious}
                    onNext={handleSelectNext}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </ContainerComponent>

      <ArtifactEditorModal
        open={hasArtifactsPaidAccess && isEditorOpen}
        mode={editorMode}
        artifact={editorMode === 'edit' ? selectedArtifact : null}
        onClose={() => setIsEditorOpen(false)}
        onSaved={(artifact) => {
          setSelectedArtifactId(artifact.id);
          setIsEditorOpen(false);
          setActionErrorMessage('');
          setIsDescriptionExpanded(false);
          setIsTranscriptExpanded(false);
          void refetch();
        }}
      />

      <ArtifactShareModal
        open={hasArtifactsPaidAccess && isShareModalOpen}
        collectionCount={items.length}
        onClose={() => setIsShareModalOpen(false)}
      />

      <Modal
        open={isDeleteModalOpen}
        title="Delete artifact"
        okText="Confirm delete"
        okButtonProps={{ danger: true, loading: isDeleting }}
        cancelText="Cancel"
        onCancel={() => setIsDeleteModalOpen(false)}
        onOk={() => void handleConfirmDelete()}
      >
        <Paragraph>
          Delete <Text strong>{selectedArtifact?.title || 'this artifact'}</Text>? This removes it from your collection.
        </Paragraph>
      </Modal>

      <Footer />
    </div>
  );
}

export default PatientArtifactsPage;